import { useI18n } from 'context/i18n';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod/v4';
import { Form } from 'react-aria-components';
import { useMutation } from '@tanstack/react-query';

import { checkProfile } from 'actions/general';
import { getOrderInfoV2 } from 'actions/order';
import { useLocale } from 'hooks/useLocale';
import { useForm } from 'hooks/use-form';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { COOKIES, eraseCookie, setCookie } from 'utils/helpers';
import { emailSchema, requiredStringSchema } from 'utils/validation';

import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { TextField } from 'components/ui/text-field';
import { Button } from 'components/ui/button';
import { getDomainValue, isDomain } from 'utils/domain';
import { Drawer, DrawerBody, DrawerHeader, DrawerRoot } from 'components/ui/drawer';
import { Link } from 'components/ui/link';

const schema = z.object({
  email: emailSchema,
  locator: requiredStringSchema,
});

type FormValues = z.infer<typeof schema>;

const isLab = isDomain('camperlab');

const trackLink = getDomainValue({
  camper: 'https://web.global-e.com/Order/Track/mZMg',
  camperlab: 'https://web.global-e.com/Order/Track/mZMg',
  nnormal: 'https://web.global-e.com/Order/Track/gsBBd',
});
const returnLink = getDomainValue({
  camper: 'https://web.global-e.com/returns/portal/mZMg',
  camperlab: 'https://web.global-e.com/returns/portal/mZMg',
  nnormal: 'https://web.global-e.com/Returns/Portal/gsBBd',
});

function GlobalEOrderContent({}: { closeModal: () => void }) {
  const { t } = useI18n();

  return (
    <div className="md:max-w-md lab:md:max-w-none">
      <p className="text-2xl nnormal:font-secondary lab:mb-2 lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs" aria-hidden>
        {t('menu.superior', 'mis.pedidos')}
      </p>
      <p>
        {t(
          'menu.superior',
          'a.traves.de',
          '***Entering the order number and the same e-mail you used to place the order, you will accessto My Orders section, where you will be able to:',
        )}
      </p>

      <ul className="flex list-inside flex-col gap-3.5">
        <li>· {t('menu.superior', 'seguimiento.de.tus.pedidos.online')}</li>
        <li>· {t('menu.superior', 'consultar.el.tracking.de.tu.pedido')}</li>
        <li>· {t('menu.superior', 'gestionar.la.devolucion.de.un.pedido')}</li>
      </ul>

      <div className="grid grid-cols-2 gap-2 lab:grid-cols-1">
        <Link variant="outline" href={trackLink}>
          {t('menu.superior', 'tracking')}
        </Link>
        <Link variant="outline" href={returnLink}>
          {t('menu.superior', 'return.portal')}
        </Link>
      </div>
    </div>
  );
}

function OrderContent({}: { closeModal: () => void }) {
  const { t } = useI18n();
  const { country, locale } = useLocale();
  const startedForm = useRef(false);

  const order = useMutation({
    mutationFn: async (values: FormValues) => {
      dataLayerHandleEvent({
        event: 'try_orders',
        event_category: 'orders',
        event_detail_1: '',
        event_detail_2: '',
      });
      dataLayerHandleEvent({
        event: 'orders',
        event_category: 'orders',
        event_detail_1: '',
        event_detail_2: '',
      });
      const orderInfoRs = await getOrderInfoV2({
        locale,
        email: values.email,
        locator: values.locator,
      });
      const { sapActive, message } = orderInfoRs;

      if (sapActive === false) {
        throw message;
      }
      const order = { email: values.email.trim(), locator: values.locator.trim() };
      eraseCookie(COOKIES.ORDER_DETAILS);
      setCookie(COOKIES.ORDER_DETAILS, encodeURI(JSON.stringify(order)), 1);
      return orderInfoRs;
    },
    onSuccess: async (data) => {
      let localeOrder = locale;
      if (data.countryId && data.languageId) {
        localeOrder = `${data.languageId}_${data.countryId}`;
      } else {
        const auxProfile = await checkProfile({
          profile: locale,
        });
        localeOrder = auxProfile.defaultProfile;
      }
      window.location.assign(`${window.location.origin}/${localeOrder}/myaccount/myorders/orderdetails`);
    },
  });
  const { handleSubmit, validate } = useForm({ schema: schema });

  const handleStartOrdersEvent = (origin: string) => {
    if (startedForm.current) return;
    startedForm.current = true;
    dataLayerHandleEvent({
      event: 'start_orders',
      event_category: 'orders',
      event_detail_1: origin,
      event_detail_2: '',
    });
  };
  return (
    <>
      <p className="text-2xl nnormal:font-secondary lab:mb-2 lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs" aria-hidden>
        {t('menu.superior', 'mis.pedidos')}
      </p>
      <div className="grid gap-9 lab:flex lab:flex-grow lab:flex-col lab:gap-5 md:max-w-lg lab:md:max-w-none 2xl:max-w-none 2xl:grid-cols-2">
        <div>
          {isLab ?
            <p className="mb-0 text-sm lg:text-xs">{t('menu.superior', 'mis.pedidos.description')}</p>
          : <div className="text-sm">
              <p>{t('menu.superior', 'a.traves.de')}</p>
              <ul className="list-inside">
                {country !== 'CN' && <li className="mt-3.5">· {t('menu.superior', 'realizar.cambios.de.talla.o.de.modelo')}</li>}
                <li className="mt-3.5">· {t('menu.superior', 'consultar.el.tracking.de.tu.pedido')}</li>
                <li className="mt-3.5">· {t('menu.superior', `gestionar.la.devolucion.de.un.pedido${country === 'MX' ? '.mx' : ''}`)}</li>
              </ul>
            </div>
          }
        </div>
        <div className="flex flex-grow flex-col">
          <p className="text-sm lab:mb-5 lab:lg:text-xs">{t('menu.superior', 'mis.pedidos.info')}</p>
          <Form onSubmit={(e) => order.mutate(handleSubmit(e))} className="flex flex-grow flex-col gap-3">
            <TextField
              variant={isLab ? 'normal' : 'box'}
              label={t('menu.superior', 'email', 'Email')}
              name="email"
              type="email"
              validate={(value) => validate('email', value)}
              onFocus={() => handleStartOrdersEvent('email')}
            />
            <TextField
              variant={isLab ? 'normal' : 'box'}
              label={t('menu.superior', 'n.de.pedido', 'Order Number')}
              name="locator"
              validate={(value) => validate('locator', value)}
              onFocus={() => handleStartOrdersEvent('code')}
            />
            <Button isPending={['success', 'pending'].includes(order.status)} type="submit" className="nnormal:font-secondary lab:mt-auto">
              {t('menu.superior', 'entrar', 'Enter')}
            </Button>
            <p className="text-error empty:hidden">{order.error?.message}</p>
          </Form>
        </div>
      </div>
    </>
  );
}

export type OpenMyOrderFn = (origin?: keyof typeof myOrderEvents) => void;

const myOrderEvents = {
  account: { event: 'main_menu', event_category: 'menu', event_detail_1: 'my account', event_detail_2: 'orders' },
  tracking: { event: 'footer_menu', event_category: 'menu', event_detail_1: 'shopping on website', event_detail_2: 'order tracking' },
};

export function MyOrderModal() {
  const {
    t,
    profileData: { global_e },
  } = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    window.openMyOrderModal = (origin?: keyof typeof myOrderEvents) => {
      if (origin && origin in myOrderEvents) {
        dataLayerHandleEvent(myOrderEvents[origin]);
      }
      setIsOpen(true);
    };
    window.showOrderPopUp = () => {
      setIsOpen(true);
    };
    return () => {
      window.openMyOrderModal = undefined;
      window.showOrderPopUp = undefined;
    };
  }, []);

  if (isLab) {
    return (
      <DrawerRoot
        className="w-full"
        isOpen={isOpen}
        onOpenChange={(isOpen) => {
          setIsOpen(isOpen);
        }}
        showOverlay
      >
        <Drawer>
          <DrawerHeader label={t('menu.superior', 'mis.pedidos', 'Orders')} className="px-2.5" />
          <DrawerBody className="mb-2.5 flex flex-col">
            {global_e ?
              <GlobalEOrderContent closeModal={() => setIsOpen(false)} />
            : <OrderContent closeModal={() => setIsOpen(false)} />}
          </DrawerBody>
        </Drawer>
      </DrawerRoot>
    );
  }

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen);
      }}
      showOverlay
      mobileFull
    >
      <Dialog>
        <DialogHeader label={t('menu.superior', 'mis.pedidos')} hidden />
        {global_e ?
          <GlobalEOrderContent closeModal={() => setIsOpen(false)} />
        : <OrderContent closeModal={() => setIsOpen(false)} />}
      </Dialog>
    </DialogRoot>
  );
}
